import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import {
    Box, Checkbox, Chip, DialogContent, DialogTitle,
    FormControlLabel, Icon, Menu, MenuItem, TextField, Tooltip, Typography,
} from '@material-ui/core';
import { DisabledFormWrapper } from 'app/main/util/clusterUtils';
import OwnerSelectionCard from 'app/shared-components/OwnerSelectionCard';

import * as formHelpers from './formHelpers';
import { isEfaCompatible, areZonesDifferent } from './formHelpers';
import useClusterForm from '../hooks/useClusterForm';
import ClusterFormHeader from '../components/ClusterFormHeader';
import QuotaWarningBanner from '../components/QuotaWarningBanner';
import AdvancedSectionsBlock from '../components/AdvancedSectionsBlock';
import BasicInformationSection from '../components/BasicInformationSection.temporary';
import ResourceManagerConfiguration from '../components/ResourceManagerConfiguration.temporary';
import StorageSettingsSection from '../components/StorageSettingsSection.temporary';
import NetworkSettingsSection from '../components/NetworkSettingsSection.temporary';
import CapacityReservationsSection from '../components/CapacityReservationsSection.temporary';
import DescriptionSection from '../components/DescriptionSection.temporary';
import CredentialSelector from '../components/CredentialSelector.temporary';
import RegionSelector from '../components/RegionSelector.temporary';
import ZoneSelector from '../components/ZoneSelector.temporary';
import InstanceSizeSelector from '../components/InstanceSizeSelector';
import SectionCard from '../components/SectionCard';
import ComputeNodesSection from '../components/ComputeNodesSection';
import ComputeNodeGroupFields from '../components/ComputeNodeGroupFields';
import VolumeManager from '../components/VolumeManager';
import HeadNodePDSSection from '../ibm/HeadNodePDSSection';
import StorageConfigurationSection from '../components/StorageConfigurationSection';
import { ZoneAvailabilityIndicator } from '../components/ZoneAvailabilityComponents';
import { inputGroupStyle, columnContainerStyle, columnStyle } from '../components/formLayoutStyles';

const AwsCluster = (props) => {
    const {
        form, setForm, setField, advanced, setAdvanced,
        classes, loading, marketplace, selectedOwner, setSelectedOwner,
        disableInputs, isFormValid, volSizeLimits, instanceSizes,
        availability, quotas, handleClose, handleSubmit, handleEdit, dispatch,
    } = useClusterForm({ provider: 'aws', props, formHelpers });

    const {
        isLoading: isAvailabilityLoading, error: availabilityError,
        unableToVerify, selectedZoneAvailable, selectedZoneMissing,
    } = availability;

    // Storage configuration state
    const [availableStorageConfigs, setAvailableStorageConfigs] = useState([]);
    const volumeDropdownAnchorEl = useRef({});
    const [volumeDropdownOpen, setVolumeDropdownOpen] = useState({});

    const zonesDiffer = useMemo(() => areZonesDifferent(form), [form]);

    // Marketplace AMI per region
    useEffect(() => {
        if (!marketplace.isMarketStack || !form?.regionId || !marketplace.config?.marketplace_images) return;
        const regionImages = marketplace.config.marketplace_images?.e4s?.[form.regionId];
        if (regionImages) {
            setField({
                marketplaceHeadImageId: regionImages.head_image_id || null,
                marketplaceComputeImageId: regionImages.compute_image_id || null,
            });
        }
    }, [marketplace.isMarketStack, marketplace.config, form?.regionId, setField]);

    // Storage-managed node sync
    const handleStorageConfigurationChange = useCallback((storageConfigurations, metadata = {}) => {
        if (!form) return;
        setAvailableStorageConfigs(storageConfigurations || []);

        const currentGroups = form.nodeGroups || [];
        const regularGroups = currentGroups.filter(g => !g.storageReference && g.nodeType !== 'media');
        const managedGroups = [];
        const processedIds = new Set();

        const genRef = (config, nodeType) => {
            if (config.id) return `${nodeType}-${config.id}`;
            const hash = JSON.stringify({ name: config.name, nodeType, mediaType: config.mediaType, stackName: config.stackName })
                .split('').reduce((a, b) => { a = ((a << 5) - a) + b.charCodeAt(0); return a & a; }, 0);
            return `${nodeType}-${config.name || 'unnamed'}-${Math.abs(hash)}`;
        };

        (storageConfigurations || []).forEach(config => {
            if (!config.mediaType || !config.stackName?.trim() ||
                (config.provider !== 'volumez' && config.providerType !== 'volumez')) return;

            const ref = genRef(config, 'media');
            processedIds.add(ref);
            const existing = currentGroups.find(g => g.storageReference === ref)
                || currentGroups.find(g => g.nodeType === 'media' && g.instance_id === config.mediaType
                    && (g.group_node_prefix === config.stackName || (!g.group_node_prefix && !config.stackName)));

            managedGroups.push({
                ...(existing || {}),
                _id: existing?._id || false,
                id: existing?.id || ref,
                instance_id: config.mediaType,
                group_node_prefix: config.stackName || existing?.group_node_prefix || '',
                group_node_prefix_label: config.name || existing?.group_node_prefix_label || `Media Storage ${config.id}`,
                count: (config.requiredNodes > 0) ? config.requiredNodes : (existing?.count || 1),
                image_type: existing?.image_type || null,
                efa: existing?.efa || false,
                zone_id: config.mediaZoneId || existing?.zone_id || null,
                storageReference: ref,
                nodeType: 'media',
            });
        });

        const validConfigIds = new Set((storageConfigurations || []).map(c => c.id));
        const allGroups = [...regularGroups, ...managedGroups].map(g => {
            if (g.attachedVolumes?.length > 0) {
                const valid = g.attachedVolumes.filter(v => validConfigIds.has(v.storageConfigId));
                if (valid.length !== g.attachedVolumes.length) return { ...g, attachedVolumes: valid };
            }
            return g;
        });
        const headVolumes = (form.serverAttachedVolumes || []).filter(v => validConfigIds.has(v.storageConfigId));
        setField({ nodeGroups: allGroups, serverAttachedVolumes: headVolumes });
    }, [form, setField]);

    // Compute node volume operations
    const addVolumeToNode = useCallback((nodeIndex, storageConfigId) => {
        const config = availableStorageConfigs.find(c => c.id === storageConfigId);
        if (!config) return;
        const headVolume = config.headNodeVolumes?.[0];
        const newVolume = {
            id: `node-${nodeIndex}-volume-${Date.now()}`,
            storageConfigId: config.id,
            mountPath: headVolume?.mountPath || '/data',
            size: headVolume?.overrides?.capacity || null,
            policyId: headVolume?.policy || '',
            overrides: { capacity: headVolume?.overrides?.capacity || null, iops: headVolume?.overrides?.iops || null, throughput: headVolume?.overrides?.throughput || null },
            status: 'pending',
        };
        const updated = [...(form.nodeGroups || [])];
        updated[nodeIndex] = { ...updated[nodeIndex], attachedVolumes: [...(updated[nodeIndex].attachedVolumes || []), newVolume] };
        setField('nodeGroups', updated);
    }, [form?.nodeGroups, availableStorageConfigs, setField]);

    const removeVolumeFromNode = useCallback((nodeIndex, volumeId) => {
        const updated = [...(form.nodeGroups || [])];
        updated[nodeIndex] = { ...updated[nodeIndex], attachedVolumes: (updated[nodeIndex].attachedVolumes || []).filter(v => v.id !== volumeId) };
        setField('nodeGroups', updated);
    }, [form?.nodeGroups, setField]);

    const handleVolumeUpdate = useCallback((nodeIndex, updatedVolume) => {
        const group = (form.nodeGroups || [])[nodeIndex];
        if (!group?.attachedVolumes) return;
        const idx = group.attachedVolumes.findIndex(v => v.id === updatedVolume.id);
        if (idx === -1) return;
        const vols = [...group.attachedVolumes];
        vols[idx] = updatedVolume;
        const updated = [...(form.nodeGroups || [])];
        updated[nodeIndex] = { ...group, attachedVolumes: vols };
        setField('nodeGroups', updated);
    }, [form?.nodeGroups, setField]);

    // AWS compute node render prop
    const renderAwsGroupFields = useCallback((group, index, { updateGroup }) => {
        const isStorageManaged = Boolean(group.storageReference);
        return (
            <>
                {isStorageManaged && (
                    <Box mb={1}><Chip label="Media Storage" size="small" style={{ backgroundColor: '#E8F5E9', color: '#2E7D32' }} /></Box>
                )}
                <ComputeNodeGroupFields
                    group={group} index={index} updateGroup={updateGroup}
                    provider="aws" imageMode={form?.imageMode} maxCount={250}
                    onInstanceChange={isStorageManaged ? undefined : (i, id) => updateGroup(i, 'instance_id', id)}
                    allowedInstanceIds={marketplace.allowedComputeIds}
                    disableStackFields={marketplace.disableStackFields || isStorageManaged}
                    disabled={disableInputs || isStorageManaged}
                >
                    <div style={inputGroupStyle}>
                        <ZoneSelector provider="aws" regionId={form?.regionId || ''}
                            value={group.zone_id || form?.zoneId || ''}
                            onChange={(zoneId) => updateGroup(index, 'zone_id', zoneId)}
                            disabled={disableInputs || isStorageManaged} />
                        {isEfaCompatible(group.instance_id, instanceSizes) && (
                            <FormControlLabel
                                control={
                                    <Checkbox style={{ color: '#2196F3' }}
                                        checked={isEfaCompatible(group.instance_id, instanceSizes) && group.efa && !zonesDiffer}
                                        onChange={() => { if (!isStorageManaged && !zonesDiffer) updateGroup(index, 'efa', !group.efa); }}
                                        disabled={isStorageManaged || zonesDiffer} />
                                }
                                label={<Tooltip title={zonesDiffer ? "EFA not supported across zones" : "Elastic Fabric Adapter"}><span>EFA</span></Tooltip>}
                            />
                        )}
                    </div>
                    {availableStorageConfigs.length > 0 && !isStorageManaged && (
                        <Box mt={2}>
                            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                                <Typography variant="subtitle2" style={{ fontWeight: 600 }}>Volumes</Typography>
                                <button
                                    onClick={(e) => {
                                        volumeDropdownAnchorEl.current[index] = e.currentTarget;
                                        setVolumeDropdownOpen(prev => ({ ...prev, [index]: true }));
                                    }}
                                    className={classes.grayButton}
                                    style={{ fontSize: '11px', padding: '4px 8px', cursor: 'pointer', border: '1px solid #ccc', borderRadius: '4px', background: '#fafafa' }}
                                >
                                    + Add Volume
                                </button>
                                <Menu
                                    anchorEl={volumeDropdownAnchorEl.current[index]}
                                    open={Boolean(volumeDropdownOpen[index])}
                                    onClose={() => setVolumeDropdownOpen(prev => ({ ...prev, [index]: false }))}
                                    PaperProps={{ style: { maxHeight: 300, minWidth: 250 } }}
                                >
                                    {availableStorageConfigs.map(config => (
                                        <MenuItem key={config.id} onClick={() => {
                                            addVolumeToNode(index, config.id);
                                            setVolumeDropdownOpen(prev => ({ ...prev, [index]: false }));
                                        }}>
                                            <Box display="flex" alignItems="center">
                                                <Icon style={{ marginRight: 8, fontSize: '16px' }}>storage</Icon>
                                                <Typography variant="body2" style={{ fontSize: '12px' }}>{config.name || 'Unknown'}</Typography>
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </Menu>
                            </Box>
                            <VolumeManager
                                volumes={group.attachedVolumes || []}
                                availableStorageConfigs={availableStorageConfigs}
                                onVolumeUpdate={handleVolumeUpdate}
                                onVolumeDelete={removeVolumeFromNode}
                                nodeGroupIndex={index}
                            />
                        </Box>
                    )}
                </ComputeNodeGroupFields>
            </>
        );
    }, [form?.imageMode, form?.regionId, form?.zoneId, marketplace, disableInputs, zonesDiffer, instanceSizes, availableStorageConfigs, addVolumeToNode, handleVolumeUpdate, removeVolumeFromNode, volumeDropdownOpen, classes.grayButton]);

    return (
        <React.Fragment>
            {form && (
                <React.Fragment>
                    <DialogTitle className={clsx(classes.modalHeader, 'h-54 min-h-54 p-20')}>
                        <ClusterFormHeader
                            provider="aws" providerLabel="Amazon Web Services" marketplace={marketplace}
                            advanced={advanced} onToggleAdvanced={() => setAdvanced(!advanced)}
                            isFormValid={isFormValid} loading={loading} disableInputs={disableInputs}
                            formType={props.type} onSave={handleSubmit} onEdit={handleEdit} onClose={handleClose}
                        />
                    </DialogTitle>

                    <QuotaWarningBanner quotas={quotas} />

                    <DialogContent className={clsx(classes.content, 'p-16')}>
                        <DisabledFormWrapper isDisabled={disableInputs}>
                            <div className="!w-full">
                                <OwnerSelectionCard
                                    value={selectedOwner} onChange={setSelectedOwner}
                                    label="Cluster Owner" title="Cluster Owner"
                                    showInAdvancedMode={true} advanced={advanced} disabled={disableInputs}
                                />
                                <BasicInformationSection
                                    form={form} setField={setField} provider="aws"
                                    allowedOsTypes={marketplace.allowedOsTypes || undefined}
                                    disableStackFields={marketplace.disableStackFields}
                                />

                                <div style={columnContainerStyle}>
                                    <div style={columnStyle}>
                                        <SectionCard icon="cloud" title="Infrastructure" fillHeight
                                            action={form.zoneId ? (
                                                <ZoneAvailabilityIndicator
                                                    isLoading={isAvailabilityLoading} isAvailable={selectedZoneAvailable}
                                                    missingInstances={selectedZoneMissing} error={availabilityError}
                                                    unableToVerify={unableToVerify} />
                                            ) : null}
                                        >
                                            <div style={inputGroupStyle}>
                                                <CredentialSelector provider="aws" value={form.credentialId} onChange={(id) => setField('credentialId', id)} />
                                                <InstanceSizeSelector provider="aws" value={form.serverInstanceId || ''}
                                                    onChange={(id) => setField('serverInstanceId', id)}
                                                    allowedInstanceIds={marketplace.config?.instance_type} label="Head Node Size" />
                                            </div>
                                            <div style={inputGroupStyle}>
                                                <RegionSelector provider="aws" value={form.regionId || ''} onChange={(id) => setField('regionId', id)}
                                                    allowedRegionIds={marketplace.config?.allowed_regions?.length > 0 ? marketplace.config.allowed_regions : undefined} />
                                                <ZoneSelector provider="aws" regionId={form.regionId || ''} value={form.zoneId || ''} onChange={(id) => setField('zoneId', id)} />
                                            </div>
                                        </SectionCard>
                                    </div>
                                    <div style={columnStyle}>
                                        <ResourceManagerConfiguration form={form} setField={setField} lockManager={Boolean(marketplace.config?.resource_manager)} />
                                    </div>
                                </div>

                                <AdvancedSectionsBlock
                                    form={form} setForm={setForm} setField={setField}
                                    provider="aws" advanced={advanced} marketplace={marketplace} disableInputs={disableInputs}
                                />

                                {advanced && (
                                    <div style={columnContainerStyle}>
                                        <div style={columnStyle}>
                                            <NetworkSettingsSection form={form} setField={setField} fillHeight />
                                        </div>
                                        <div style={columnStyle}>
                                            <CapacityReservationsSection form={form} setField={setField} fillHeight showAutoExpire />
                                        </div>
                                    </div>
                                )}
                                {advanced && (
                                    <StorageSettingsSection form={form} setField={setField} provider="aws"
                                        volSizeLimits={volSizeLimits} shareOptions={[{ value: 'nfs', label: 'NFS' }]} />
                                )}
                                {advanced && (
                                    <StorageConfigurationSection
                                        value={form.storageConfigurations || []}
                                        onChange={handleStorageConfigurationChange}
                                        disabled={disableInputs} cloudProvider="aws"
                                        regionId={form.regionId} zoneId={form.zoneId} />
                                )}

                                <HeadNodePDSSection form={form} setField={setField} />

                                <ComputeNodesSection form={form} setField={setField} disabled={disableInputs}
                                    maxGroups={250} renderGroupFields={renderAwsGroupFields} />

                                <DescriptionSection form={form} setField={setField} />
                            </div>
                        </DisabledFormWrapper>
                    </DialogContent>
                </React.Fragment>
            )}
        </React.Fragment>
    );
};

export default AwsCluster;
